import { api } from './api';

export interface Setting {
  key: string;
  value: string;
  valueType: 'string' | 'boolean' | 'number';
  category: string;
  description: string;
  options?: string[];
  updatedAt?: string;
  updatedBy?: string;
  source: 'database' | 'environment' | 'default';
  disabledByProvider?: boolean;
  providerConstraintMessage?: string;
  disabledByDependency?: boolean;
  dependencyMessage?: string;
  helpText?: string;
  requires?: string;
}

export interface Secret {
  key: string;
  category: string;
  description: string;
  testable: boolean;
  hasValue: boolean;
  updatedAt?: string;
  updatedBy?: string;
  source: 'database' | 'environment' | 'not_set';
}

export interface SettingsResponse {
  settings: Record<string, Setting[]>;
  categories: string[];
}

export interface SecretsResponse {
  secrets: Record<string, Secret[]>;
  categories: string[];
  encryptionEnabled: boolean;
}

export interface SettingsStatus {
  secretsEnabled: boolean;
  bossKeyConfigured: boolean;
  settingsCount: number;
  secretsCount: number;
  categories: {
    settings: string[];
    secrets: string[];
  };
  email?: {
    provider: string | null;
    hasResendKey: boolean;
    hasSmtpCredentials: boolean;
    hasAppOrigin: boolean;
    warning: string | null;
  };
}

export interface TestResult {
  success: boolean;
  message: string;
}

export const settingsService = {
  async getSettings(category?: string): Promise<SettingsResponse> {
    const params = category ? { category } : {};
    const response = await api.get('/super-admin/settings', { params });
    return response.data;
  },

  async updateSettings(settings: Array<{ key: string; value: string | boolean | number }>): Promise<{ results: Array<{ key: string; success: boolean; error?: string }> }> {
    const response = await api.put('/super-admin/settings', { settings });
    return response.data;
  },

  async getSecrets(category?: string): Promise<SecretsResponse> {
    const params = category ? { category } : {};
    const response = await api.get('/super-admin/secrets', { params });
    return response.data;
  },

  async updateSecret(key: string, value: string, category: string, description?: string): Promise<{ success: boolean; key: string }> {
    const response = await api.put('/super-admin/secrets', { key, value, category, description });
    return response.data;
  },

  async deleteSecret(key: string): Promise<void> {
    await api.delete(`/super-admin/secrets/${key}`);
  },

  async testSecret(key: string): Promise<TestResult> {
    const response = await api.post(`/super-admin/secrets/${key}/test`);
    return response.data;
  },

  async getStatus(): Promise<SettingsStatus> {
    const response = await api.get('/super-admin/settings/status');
    return response.data;
  },

  async generateVapidKeys(): Promise<{ success: boolean; publicKey: string; message: string }> {
    const response = await api.post('/super-admin/settings/generate-vapid-keys');
    return response.data;
  },

  async resetCircuitBreakers(): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/super-admin/reset-circuit-breakers');
    return response.data;
  },

  async clearTileCache(): Promise<{ success: boolean; clearedCount: number; message: string }> {
    const response = await api.post('/super-admin/clear-tile-cache');
    return response.data;
  },
};
